/* eslint-disable */
import { useCallback, useEffect, useState } from "react";
import {
  HardHat,
  Shield,
  Wrench,
  Sparkles as SparklesIcon,
  UserPlus,
  Phone,
  MessageCircle,
  Clock,
} from "lucide-react";
import { operationsApi } from "./api";
import { ticketsApi } from "./ticketsApi";
import { useAuth } from "../auth/AuthContext";
import StaffCard from "./StaffCard";
import AddStaffDialog from "./AddStaffDialog";
import DutyRosterForm from "./DutyRosterForm";
import TicketsTab from "./TicketsTab";
import "./OperationsPage.css";

const TABS = [
  { id: "tickets", label: "Maintenance Tickets" },
  { id: "current", label: "Staff" },
  { id: "roster",  label: "Duty Roster" },
  { id: "categories", label: "Work Categories", fmOnly: true },
];

const SECTION_ICONS = {
  SECURITY: Shield,
  MAINTENANCE: Wrench,
  HOUSEKEEPING: SparklesIcon,
  OTHER: HardHat,
};

export default function OperationsPage() {
  const { activeRole } = useAuth();
  const [tab, setTab] = useState("tickets");
  const [data, setData] = useState(null);
  const [duty, setDuty] = useState([]);
  const [onCallList, setOnCallList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showRosterForm, setShowRosterForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [s, t, oc] = await Promise.all([
        operationsApi.listStaff(),
        operationsApi.dutyToday(),
        operationsApi.onCall().catch(() => []),
      ]);
      setData(s);
      setDuty(t || []);
      setOnCallList(Array.isArray(oc) ? oc : []);
    } catch (e) {
      setError(e.message || "Could not load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Management controls reflect the *active view*, not just raw capability:
  // an admin/FM acting as a resident sees the read-only directory. Each
  // capability is matched to the role that unlocks it; the backend enforces
  // the same scope via the signed `act` claim. Legacy sessions (no active
  // role) fall back to capability so nobody loses access mid-rollout.
  const canManage = activeRole
    ? (activeRole === "ADMIN" && !!data?.is_admin) ||
      (activeRole === "FACILITY_MANAGER" && !!data?.is_facility_manager) ||
      (activeRole === "SECURITY_INCHARGE" && !!data?.is_security_incharge)
    : !!(data?.is_admin || data?.is_facility_manager || data?.is_security_incharge);
  // The security incharge may add line guards only — the Add-staff dialog
  // restricts its role options accordingly (the backend enforces the same).
  const addRoleScope =
    activeRole === "SECURITY_INCHARGE" && !data?.is_admin && !data?.is_facility_manager
      ? "guards_only"
      : "all";
  // Quick lookup of who's on duty today, keyed by staff_id, so cards can
  // surface a badge without a second round trip.
  const dutyByStaff = duty.reduce((acc, r) => {
    if (!acc[r.staff_id]) acc[r.staff_id] = [];
    acc[r.staff_id].push(r.shift);
    return acc;
  }, {});

  return (
    <div className="vsop-shell">
      <div className="vsop-head">
        <div>
          <h1>Operations</h1>
          <p className="vsop-sub">
            Maintenance tickets and the staff who keep Vishal Sanjivini
            running, led by the Facility Manager.
          </p>
        </div>
      </div>

      <div className="vsop-tabs" role="tablist">
        {TABS.filter((t) => !t.fmOnly || canManage).map((t) => (
          <button
            key={t.id}
            className={`vsop-tab ${tab === t.id ? "active" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.id === "roster" && duty.length > 0 && (
              <span className="vsop-tab-count">{duty.length}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "tickets" && <TicketsTab />}

      {error && tab !== "tickets" && <div className="vsop-error">{error}</div>}
      {loading && tab !== "tickets" && <div className="vsop-empty">Loading…</div>}

      {!loading && tab === "current" && data && (
        <CurrentStaffView
          data={data}
          dutyByStaff={dutyByStaff}
          onCallList={onCallList}
          canManage={canManage}
          onAdd={() => setShowAdd(true)}
          onChanged={load}
        />
      )}

      {!loading && tab === "roster" && (
        <RosterView
          duty={duty}
          canManage={canManage}
          onEdit={() => setShowRosterForm(true)}
        />
      )}

      {tab === "categories" && canManage && <WorkCategoriesTab />}

      {showAdd && (
        <AddStaffDialog
          roleScope={addRoleScope}
          onClose={() => setShowAdd(false)}
          onSaved={() => {
            setShowAdd(false);
            load();
          }}
        />
      )}

      {showRosterForm && (
        <DutyRosterForm
          onClose={() => setShowRosterForm(false)}
          onSaved={() => {
            setShowRosterForm(false);
            load();
          }}
        />
      )}
    </div>
  );
}

function CurrentStaffView({ data, dutyByStaff, onCallList, canManage, onAdd, onChanged }) {
  return (
    <>
      {onCallList.length > 0 && (
        <div className="vsop-oncall-banner">
          <span className="vsop-oncall-label">
            <Phone size={13} /> On call today
          </span>
          {onCallList.map((c) => (
            <a
              key={c.staff_id}
              className="vsop-oncall-chip"
              href={`tel:${c.staff_phone}`}
              title={`Call ${c.staff_name}`}
            >
              <span className="vsop-oncall-name">{c.staff_name}</span>
              <span className="vsop-oncall-role">{c.staff_role_label}</span>
              <Phone size={11} className="vsop-oncall-phone-ic" />
            </a>
          ))}
        </div>
      )}

      {canManage && (
        <div className="vsop-staff-actions">
          <button className="vsop-btn-primary" onClick={onAdd}>
            <UserPlus size={14} /> Add staff
          </button>
        </div>
      )}

      {data.facility_manager && (
        <section className="vsop-fm-section">
          <div className="vsop-section-head">
            <HardHat size={16} />
            <h2>Facility Manager</h2>
          </div>
          <StaffCard
            member={data.facility_manager}
            shifts={dutyByStaff[data.facility_manager.id]}
            canManage={canManage}
            onChanged={onChanged}
            featured
          />
        </section>
      )}

      {data.sections
        .filter((s) => s.key !== "SECURITY")
        .map((s) => {
        const Icon = SECTION_ICONS[s.key] || HardHat;
        return (
          <section key={s.key} className="vsop-section">
            <div className="vsop-section-head">
              <Icon size={16} />
              <h2>{s.label}</h2>
              <span className="vsop-section-count">{s.members.length}</span>
            </div>
            <div className="vsop-grid">
              {s.members.map((m) => (
                <StaffCard
                  key={m.id}
                  member={m}
                  shifts={dutyByStaff[m.id]}
                  canManage={canManage}
                  onChanged={onChanged}
                />
              ))}
            </div>
          </section>
        );
      })}

      {!data.facility_manager && data.sections.length === 0 && (
        <div className="vsop-empty-card">
          <HardHat size={28} strokeWidth={1.4} />
          <h3>No staff added yet</h3>
          <p>
            {canManage
              ? "Click Add staff to seed the operations roster."
              : "An admin will add staff details soon."}
          </p>
        </div>
      )}
    </>
  );
}

function RosterView({ duty, canManage, onEdit }) {
  if (duty.length === 0) {
    return (
      <div className="vsop-empty-card">
        <Clock size={28} strokeWidth={1.4} />
        <h3>No duty roster set for today</h3>
        <p>
          {canManage
            ? "Use Today's roster above to assign shifts."
            : "The Facility Manager will set today's roster shortly."}
        </p>
      </div>
    );
  }
  const byShift = duty.reduce((acc, r) => {
    if (!acc[r.shift]) acc[r.shift] = [];
    acc[r.shift].push(r);
    return acc;
  }, {});
  const SHIFT_ORDER = ["MORNING", "AFTERNOON", "NIGHT", "FULL_DAY"];
  return (
    <div className="vsop-roster">
      {SHIFT_ORDER.filter((s) => byShift[s]).map((s) => (
        <div key={s} className="vsop-shift">
          <div className="vsop-shift-head">
            <Clock size={13} /> {s.replace("_", " ")}
          </div>
          <ul>
            {byShift[s].map((r) => (
              <li key={r.id}>
                <strong>{r.staff_name}</strong>
                <span className="vsop-shift-role">
                  {r.staff_role.replace("_", " ")}
                </span>
                <div className="vsop-shift-actions">
                  <a
                    href={`tel:+91${r.staff_phone}`}
                    className="vsop-link vsop-link-icon"
                    aria-label="Call"
                    title="Call"
                  >
                    <Phone size={15} />
                  </a>
                  <a
                    href={`https://wa.me/91${r.staff_phone}`}
                    target="_blank"
                    rel="noreferrer"
                    className="vsop-link vsop-link-icon"
                    aria-label="WhatsApp"
                    title="WhatsApp"
                  >
                    <MessageCircle size={15} />
                  </a>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {canManage && (
        <button className="vsop-btn-secondary vsop-roster-edit" onClick={onEdit}>
          Edit today's roster
        </button>
      )}
    </div>
  );
}

function WorkCategoriesTab() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await ticketsApi.listCategories();
      setCats(data.categories || data || []);
    } catch (e) {
      setError(e.message || "Could not load categories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setSaving(true);
    try {
      await ticketsApi.createCategory({ label });
      setNewLabel("");
      setCreating(false);
      load();
    } catch (e) {
      alert(e.message || "Could not create category");
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    const label = editLabel.trim();
    if (!label || !editId) return;
    setSaving(true);
    try {
      await ticketsApi.updateCategory(editId, { label });
      setEditId(null);
      load();
    } catch (e) {
      alert(e.message || "Could not update category");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (cat) => {
    try {
      await ticketsApi.updateCategory(cat.id, { is_active: !cat.is_active });
      load();
    } catch (e) {
      alert(e.message || "Could not update category");
    }
  };

  const del = async (cat) => {
    if (!window.confirm(`Delete category "${cat.label}"?`)) return;
    try {
      await ticketsApi.deleteCategory(cat.id);
      load();
    } catch (e) {
      alert(e.message || "Could not delete category");
    }
  };

  return (
    <div className="vsop-cats-tab">
      <div className="vsop-cats-head">
        <h3>Work Categories</h3>
        {!creating && (
          <button className="vsop-btn-primary" onClick={() => setCreating(true)}>
            <span>+ New category</span>
          </button>
        )}
      </div>
      {creating && (
        <div className="vsop-cats-create-row">
          <input
            autoFocus
            type="text"
            placeholder="Category label, e.g. Plumbing"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") create();
              if (e.key === "Escape") { setCreating(false); setNewLabel(""); }
            }}
          />
          <button className="vsop-btn-primary" onClick={create} disabled={!newLabel.trim() || saving}>
            {saving ? "Creating…" : "Create"}
          </button>
          <button className="vsop-btn-ghost" onClick={() => { setCreating(false); setNewLabel(""); }}>
            Cancel
          </button>
        </div>
      )}
      {error && <div className="vsop-error">{error}</div>}
      {loading ? (
        <div className="vsop-empty">Loading…</div>
      ) : cats.length === 0 ? (
        <div className="vsop-empty">No categories yet. Create one to get started.</div>
      ) : (
        <ul className="vsop-cats-list">
          {cats.map((cat) => (
            <li key={cat.id} className={`vsop-cat-row ${cat.is_active ? "" : "vsop-cat-inactive"}`}>
              {editId === cat.id ? (
                <div className="vsop-cats-create-row">
                  <input
                    autoFocus
                    type="text"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit();
                      if (e.key === "Escape") setEditId(null);
                    }}
                  />
                  <button className="vsop-btn-primary" onClick={saveEdit} disabled={!editLabel.trim() || saving}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button className="vsop-btn-ghost" onClick={() => setEditId(null)}>Cancel</button>
                </div>
              ) : (
                <>
                  <span className="vsop-cat-label">{cat.label}</span>
                  {!cat.is_active && <span className="vsop-cat-badge">Inactive</span>}
                  <div className="vsop-cat-actions">
                    <button className="vsop-cat-btn" title="Rename" onClick={() => { setEditId(cat.id); setEditLabel(cat.label); }}>
                      <Wrench size={13} />
                    </button>
                    <button className="vsop-cat-btn" title={cat.is_active ? "Deactivate" : "Activate"} onClick={() => toggleActive(cat)}>
                      {cat.is_active ? <Clock size={13} /> : <Shield size={13} />}
                    </button>
                    <button className="vsop-cat-btn vsop-cat-btn-danger" title="Delete" onClick={() => del(cat)}>
                      <HardHat size={13} />
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
